import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import BrandedVoucherLayout, { SectionTitle, InfoCard } from '@/components/voucher/BrandedVoucherLayout';
import { Download, Clock, Car, Phone, MapPin, Plane, Wallet, Droplets, Wind } from 'lucide-react';
import { WeatherIcon, weatherLabel, extractWeatherCities, getCityWeather } from '@/utils/weather';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ACTIVITY_TYPE_LABEL = {
  pickup: 'Pickup', dropoff: 'Drop-off', activity: 'Activity', meal: 'Meal',
  flight: 'Flight', checkin: 'Check-in', checkout: 'Check-out', free: 'Free time', other: 'Other',
};

const has = (v) => (Array.isArray(v) ? v.length > 0 : !!(v && String(v).trim()));

function sortByTime(activities) {
  return [...(activities || [])].sort((a, b) =>
    String(a.time || '99:99').localeCompare(String(b.time || '99:99')));
}

function fmtTime(t) {
  if (!t) return '';
  const m = /^(\d{1,2}):(\d{2})$/.exec(String(t).trim());
  if (!m) return t;
  let h = parseInt(m[1], 10);
  const ap = h >= 12 ? 'PM' : 'AM';
  h = h % 12 || 12;
  return `${h}:${m[2]} ${ap}`;
}

// Print-optimised section frame — never split a section across pages.
const Section = ({ title, primary, testId, children }) => (
  <section className="pdf-no-break" style={{ breakInside: 'avoid', pageBreakInside: 'avoid', marginBottom: '32px' }} data-testid={testId}>
    <SectionTitle primary={primary}>{title}</SectionTitle>
    {children}
  </section>
);

const Rich = ({ html }) => (
  <div className="prose prose-sm max-w-none text-slate-700 text-sm leading-relaxed" dangerouslySetInnerHTML={{ __html: html }} />
);

// ---------------------------------------------------------------------------
// Extracted row/card components — keeping each map shallow avoids the
// visual-edits Babel "Maximum call stack size exceeded" crash on deeply
// nested inline .map() blocks.
// ---------------------------------------------------------------------------
const ActivityLine = ({ a, accent }) => {
  const typeLabel = ACTIVITY_TYPE_LABEL[a.activity_type] || a.activity_type;
  const dur = a.duration_mins;
  return (
    <div className="flex gap-3 text-sm">
      <div className="w-20 shrink-0 flex items-center gap-1 text-slate-500 font-medium">
        <Clock className="h-3.5 w-3.5 text-slate-300" />{fmtTime(a.time) || '—'}
      </div>
      <div className="min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-semibold text-slate-800">{a.title || 'Activity'}</span>
          {has(a.activity_type) && (
            <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded" style={{ background: '#EEF2F1', color: accent }}>
              {typeLabel}
            </span>
          )}
          {dur != null && dur !== '' && <span className="text-xs text-slate-400">{dur} mins</span>}
        </div>
        {has(a.location) && (
          <div className="text-xs text-slate-500 flex items-center gap-1 mt-0.5">
            <MapPin className="h-3 w-3 text-slate-300" />{a.location}
          </div>
        )}
        {has(a.description) && <div className="text-xs text-slate-500 mt-0.5">{a.description}</div>}
      </div>
    </div>
  );
};

const VehicleBlock = ({ v, accent, index }) => (
  <div className="mt-3 pt-3 border-t border-dashed border-slate-200" data-testid={`voucher-day-${index}-vehicle`}>
    <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide mb-2" style={{ color: accent }}>
      <Car className="h-3.5 w-3.5" /> Vehicle
    </div>
    <div className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-1 text-sm text-slate-700">
      {has(v.type) && <div><span className="text-slate-400">Type: </span>{v.type}</div>}
      {has(v.number_plate) && <div><span className="text-slate-400">Plate: </span><span className="font-mono">{v.number_plate}</span></div>}
      {has(v.driver_name) && <div><span className="text-slate-400">Driver: </span>{v.driver_name}</div>}
      {has(v.driver_phone) && <div className="flex items-center gap-1"><Phone className="h-3 w-3 text-slate-300" />{v.driver_phone}</div>}
      {has(v.pickup_point) && <div><span className="text-slate-400">Pickup: </span>{v.pickup_point}</div>}
      {has(v.pickup_time) && <div><span className="text-slate-400">Pickup time: </span>{fmtTime(v.pickup_time)}</div>}
      {has(v.notes) && <div className="col-span-2 md:col-span-3"><span className="text-slate-400">Notes: </span>{v.notes}</div>}
    </div>
  </div>
);

const DayCard = ({ day, index, primary, accent }) => {
  const acts = sortByTime(day.activities);
  const v = day.vehicle;
  const showVehicle = v && (has(v.type) || has(v.number_plate) || has(v.driver_name) || has(v.pickup_point));
  return (
    <div className="day-card rounded-xl border border-slate-200 overflow-hidden" data-testid={`voucher-day-${index}`}>
      <div className="flex items-center gap-3 px-4 py-2.5" style={{ background: '#FAFAFA', borderBottom: '1px solid #E5E7EB' }}>
        <span className="w-7 h-7 rounded-md text-white text-xs font-bold flex items-center justify-center" style={{ background: primary }}>
          {day.dayNumber}
        </span>
        <div className="font-semibold text-slate-800 text-sm">{day.title || `Day ${day.dayNumber}`}</div>
        {has(day.date) && <div className="text-xs text-slate-400 ml-auto">{day.date}</div>}
      </div>
      <div className="px-4 py-3">
        {has(day.description) && <p className="text-sm text-slate-600 mb-3">{day.description}</p>}
        {acts.length > 0 ? (
          <div className="space-y-2.5">
            {acts.map((a, j) => <ActivityLine key={j} a={a} accent={accent} />)}
          </div>
        ) : (!has(day.description) && <p className="text-sm text-slate-400 italic">No activities listed.</p>)}
        {showVehicle && <VehicleBlock v={v} accent={accent} index={index} />}
      </div>
    </div>
  );
};

const EmergencyRow = ({ c }) => (
  <div className="flex items-start gap-4 px-4 py-2.5 text-sm border-b border-slate-100 last:border-0">
    <div className="font-semibold text-slate-800 w-48 shrink-0">{c.label || '—'}</div>
    <div className="font-mono text-slate-700 w-40 shrink-0">{c.number || '—'}</div>
    {has(c.notes) && <div className="text-slate-500">{c.notes}</div>}
  </div>
);

const HotelCard = ({ h, index }) => {
  const checkIn = [h.checkInDate, fmtTime(h.checkin_time)].filter(Boolean).join(' · ');
  const checkOut = [h.checkOutDate, fmtTime(h.checkout_time)].filter(Boolean).join(' · ');
  return (
    <div className="rounded-xl border border-slate-200 p-4" data-testid={`voucher-hotel-${index}`}>
      <div className="font-semibold text-slate-800 text-sm mb-2">{h.hotelName || `Hotel ${index + 1}`}</div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-1.5 text-sm text-slate-700">
        {has(h.confirmation_number) && <div><span className="text-slate-400">Confirmation #: </span><span className="font-mono">{h.confirmation_number}</span></div>}
        {has(h.hotel_phone) && <div className="flex items-center gap-1"><Phone className="h-3 w-3 text-slate-300" />{h.hotel_phone}</div>}
        {has(h.mealPlan) && <div><span className="text-slate-400">Meal plan: </span>{h.mealPlan}</div>}
        {has(checkIn) && <div><span className="text-slate-400">Check-in: </span>{checkIn}</div>}
        {has(checkOut) && <div><span className="text-slate-400">Check-out: </span>{checkOut}</div>}
        {has(h.roomOccupancy) && <div><span className="text-slate-400">Occupancy: </span>{h.roomOccupancy}</div>}
        {has(h.hotel_address) && <div className="col-span-2 md:col-span-3 flex items-start gap-1"><MapPin className="h-3 w-3 mt-0.5 text-slate-300" />{h.hotel_address}</div>}
      </div>
    </div>
  );
};

const TransportRow = ({ t, primary }) => (
  <div className="flex items-start gap-4 px-4 py-2.5 text-sm border-b border-slate-100 last:border-0">
    <div className="font-semibold text-slate-800 w-40 shrink-0">{t.mode || '—'}</div>
    <div className="text-slate-600 flex-1">{t.description}</div>
    {has(t.approx_cost) && <div className="text-slate-700 w-28 shrink-0 text-right">{t.approx_cost}</div>}
    <div className="w-20 shrink-0 text-right text-xs">
      {t.available === false
        ? <span className="text-slate-400">Unavailable</span>
        : <span style={{ color: primary }}>Available</span>}
    </div>
  </div>
);

const PlaceCard = ({ p, accent }) => {
  const cat = has(p.category) ? String(p.category).replace('_', ' ') : '';
  return (
    <div className="rounded-xl border border-slate-200 p-3.5">
      <div className="flex items-center justify-between gap-2 mb-1">
        <div className="font-semibold text-slate-800 text-sm">{p.name || 'Place'}</div>
        <div className="flex items-center gap-1.5">
          {has(p.category) && <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded" style={{ background: '#EEF2F1', color: accent }}>{cat}</span>}
          {has(p.price_range) && <span className="text-[10px] uppercase tracking-wide text-slate-400">{p.price_range}</span>}
        </div>
      </div>
      {has(p.description) && <p className="text-sm text-slate-600">{p.description}</p>}
      {has(p.address) && <div className="text-xs text-slate-500 flex items-start gap-1 mt-1"><MapPin className="h-3 w-3 mt-0.5 text-slate-300" />{p.address}</div>}
      {has(p.notes) && <div className="text-xs text-slate-400 mt-1">{p.notes}</div>}
    </div>
  );
};

const CarryTag = ({ label }) => (
  <span className="text-sm px-3 py-1 rounded-full border border-slate-200 text-slate-700 bg-slate-50">{label}</span>
);

// Coerce any stored things_to_carry shape into [{ name, items }]. Legacy flat
// string lists collapse to a single "Essentials" category. Empty categories
// (agent left them blank) are dropped so the voucher stays clean.
function normalizeCarryCats(value) {
  if (!Array.isArray(value) || value.length === 0) return [];
  if (typeof value[0] === 'string') return [{ name: 'Essentials', items: value.filter(Boolean) }];
  return value
    .map((c) => ({ name: c.name || '', items: Array.isArray(c.items) ? c.items.filter(Boolean) : [] }))
    .filter((c) => c.items.length > 0);
}

const CarryCategoryBlock = ({ name, items }) => {
  const list = Array.isArray(items) ? items : [];
  return (
    <div className="space-y-2">
      {has(name) && <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">{name}</div>}
      <div className="flex flex-wrap gap-2">
        {list.map((t, i) => <CarryTag key={i} label={t} />)}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Weather — keyless Open-Meteo current conditions + 7-day outlook per city.
// ---------------------------------------------------------------------------
const WEEKDAY = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const OutlookDay = ({ date, code, tmax, tmin }) => {
  const d = new Date(`${date}T00:00:00`);
  const wd = isNaN(d.getTime()) ? '' : WEEKDAY[d.getDay()];
  return (
    <div className="flex flex-col items-center gap-1 min-w-[44px]">
      <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">{wd}</span>
      <WeatherIcon code={code} className="h-4 w-4 text-slate-500" strokeWidth={1.75} />
      <span className="text-xs font-medium text-slate-700">{tmax != null ? `${Math.round(tmax)}°` : '—'}</span>
      <span className="text-[11px] text-slate-400">{tmin != null ? `${Math.round(tmin)}°` : ''}</span>
    </div>
  );
};

const CityWeatherCard = ({ w, primary }) => {
  const cur = w.current || {};
  const daily = w.daily || {};
  const times = Array.isArray(daily.time) ? daily.time : [];
  const codes = Array.isArray(daily.weather_code) ? daily.weather_code : [];
  const maxes = Array.isArray(daily.temperature_2m_max) ? daily.temperature_2m_max : [];
  const mins = Array.isArray(daily.temperature_2m_min) ? daily.temperature_2m_min : [];
  const sliced = times.slice(0, 7);
  const outlook = sliced.map((t, i) => ({ date: t, code: codes[i], tmax: maxes[i], tmin: mins[i] }));
  return (
    <div className="rounded-xl border border-slate-200 p-4" data-testid="weather-card">
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="min-w-0">
          <div className="font-semibold text-slate-800 text-sm truncate">{w.city}</div>
          {has(w.country) && <div className="text-xs text-slate-400">{w.country}</div>}
        </div>
        <span className="text-[10px] uppercase tracking-wide text-slate-400">Now</span>
      </div>
      <div className="flex items-center gap-4 pb-3 border-b border-slate-100">
        <WeatherIcon code={cur.weather_code} className="h-10 w-10 shrink-0" strokeWidth={1.5} style={{ color: primary }} />
        <div className="min-w-0">
          <div className="text-2xl font-bold text-slate-800 leading-none">
            {cur.temperature_2m != null ? `${Math.round(cur.temperature_2m)}°C` : '—'}
          </div>
          <div className="text-xs text-slate-500 mt-1">{weatherLabel(cur.weather_code)}</div>
        </div>
        <div className="ml-auto text-right space-y-1">
          {cur.relative_humidity_2m != null && (
            <div className="flex items-center justify-end gap-1 text-xs text-slate-500">
              <Droplets className="h-3 w-3 text-slate-300" />{cur.relative_humidity_2m}%
            </div>
          )}
          {cur.wind_speed_10m != null && (
            <div className="flex items-center justify-end gap-1 text-xs text-slate-500">
              <Wind className="h-3 w-3 text-slate-300" />{Math.round(cur.wind_speed_10m)} km/h
            </div>
          )}
        </div>
      </div>
      {outlook.length > 0 && (
        <div className="flex justify-between gap-1 pt-3 overflow-x-auto">
          {outlook.map((o, i) => <OutlookDay key={i} date={o.date} code={o.code} tmax={o.tmax} tmin={o.tmin} />)}
        </div>
      )}
    </div>
  );
};

const WeatherSection = ({ destination, days, primary }) => {
  const [list, setList] = useState([]);
  const [done, setDone] = useState(false);
  // Primitive string key — stable across re-renders even though `days` is a
  // fresh array each render (avoids an infinite fetch/setState loop).
  const cityKey = extractWeatherCities(destination, days).join('|');

  useEffect(() => {
    let alive = true;
    const cities = cityKey ? cityKey.split('|') : [];
    if (cities.length === 0) { setDone(true); return undefined; }
    Promise.all(cities.map((c) => getCityWeather(c)))
      .then((results) => {
        if (!alive) return;
        // Dedupe by resolved city name.
        const byName = new Map();
        for (const r of results) {
          if (!r) continue;
          const key = String(r.city || '').toLowerCase();
          if (!byName.has(key)) byName.set(key, r);
        }
        let ok = Array.from(byName.values());
        // Drop redundant country-centroid cards (e.g. "Indonesia" from
        // "Bali, Indonesia") when an actual city in that country is present.
        const isCentroid = (r) => String(r.city).toLowerCase() === String(r.country).toLowerCase();
        const cityCountries = new Set(
          ok.filter((r) => !isCentroid(r)).map((r) => String(r.country).toLowerCase()),
        );
        ok = ok.filter((r) => !(isCentroid(r) && cityCountries.has(String(r.country).toLowerCase())));
        setList(ok);
        setDone(true);
      })
      .catch(() => { if (alive) setDone(true); });
    return () => { alive = false; };
  }, [cityKey]);

  // Render nothing until at least one city resolves — keeps the PDF/print
  // free of an empty box when weather can't be fetched in time.
  if (!done || list.length === 0) return null;

  return (
    <Section title="Weather at Your Destination" primary={primary} testId="section-weather">
      <p className="text-xs text-slate-400 mb-3">Current conditions with a 7-day outlook, fetched live when this voucher was opened.</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {list.map((w, i) => <CityWeatherCard key={i} w={w} primary={primary} />)}
      </div>
    </Section>
  );
};

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------
export default function VoucherPreview() {
  const { tripid } = useParams();
  const [searchParams] = useSearchParams();
  const autoPrint = searchParams.get('print') === '1';

  const [data, setData] = useState(null);
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState(null);
  const [retryNonce, setRetryNonce] = useState(0);

  useEffect(() => {
    document.body.classList.add('pdf-public-route');
    return () => document.body.classList.remove('pdf-public-route');
  }, []);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const [vRes, cRes] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/trip/${encodeURIComponent(tripid)}/voucher`),
          axios.get(`${BACKEND_URL}/api/public/company`).catch(() => ({ data: {} })),
        ]);
        setData(vRes.data);
        setCompany(cRes.data);
      } catch (err) {
        // Only a genuine 404 means there's really no voucher for this trip —
        // any other failure (network blip, 500, timeout) is transient and
        // must say so, not claim data doesn't exist when it might.
        if (err.response?.status === 404) {
          setError('Voucher not found for this trip.');
        } else {
          setError('Something went wrong loading this voucher. Please try again.');
        }
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [tripid, retryNonce]);

  useEffect(() => {
    if (data) document.title = data.booking_ref || data.displayTripId || data.itinerary_ref || 'Travel Voucher';
  }, [data]);

  useEffect(() => {
    if (autoPrint && data) {
      const t = setTimeout(() => window.print(), 800);
      return () => clearTimeout(t);
    }
  }, [autoPrint, data]);

  const handleDownload = async () => {
    setDownloading(true);
    setDownloadError(null);
    try {
      const res = await axios.get(
        `${BACKEND_URL}/api/trip/${encodeURIComponent(tripid)}/voucher/pdf?section=itinerary`,
        { responseType: 'blob' },
      );
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${String(tripid).replace(/[^a-zA-Z0-9-_]/g, '-')}-voucher.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      // responseType:'blob' means axios stores an error body as a Blob too —
      // decode it so the real backend detail reaches the user.
      let message = 'Could not generate the PDF. Please try again.';
      if (e.response?.data instanceof Blob) {
        try {
          const text = await e.response.data.text();
          const parsed = JSON.parse(text);
          if (parsed?.detail) message = parsed.detail;
        } catch (_) { /* not JSON — keep generic message */ }
      }
      setDownloadError(message);
    } finally {
      setDownloading(false);
    }
  };

  if (loading) return (
    <div className="min-h-screen bg-slate-100 flex items-center justify-center">
      <div className="w-8 h-8 border-4 border-teal-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );
  if (error || !data) return (
    <div className="min-h-screen bg-slate-100 flex items-center justify-center">
      <div className="flex flex-col items-center gap-3 text-center px-4">
        <p className="text-slate-500" data-testid="voucher-error">{error || 'Voucher not found'}</p>
        {error && error !== 'Voucher not found for this trip.' && (
          <button
            type="button"
            onClick={() => setRetryNonce((n) => n + 1)}
            data-testid="voucher-retry-btn"
            className="text-sm font-medium text-teal-600 hover:text-teal-700 underline"
          >
            Try again
          </button>
        )}
      </div>
    </div>
  );

  const primary = company?.primaryColor || '#34D0CB';
  const accent = company?.accentColor || '#595A5B';
  const coord = data.coordinator || {};
  const cur = data.local_currency || {};
  const hasCoord = has(coord.name) || has(coord.phone) || has(coord.whatsapp) || has(coord.email) || has(coord.available_hours);
  const hasCurrency = has(cur.currency_name) || has(cur.currency_code) || cur.exchange_rate != null || has(cur.atm_tips) || has(cur.exchange_tips);
  const travelDates = [data.travel_start, data.travel_end].filter(Boolean).join('  to  ');

  // Map over local identifiers (never member expressions like data.days) so
  // the visual-edits Babel plugin's array-iteration analysis does not recurse
  // infinitely ("Maximum call stack size exceeded").
  const emergencyContacts = data.emergency_contacts || [];
  const days = data.days || [];
  const hotels = data.hotels || [];
  const carryCategories = normalizeCarryCats(data.things_to_carry);
  const transportOptions = data.transport_options || [];
  const recommendedPlaces = data.recommended_places || [];

  const layoutVoucher = {
    type: 'itinerary',
    status: data.voucher_status || 'confirmed',
    destination: data.destination,
    guestName: data.guest_name,
    trvRef: data.booking_ref || data.displayTripId || data.itinerary_ref,
    voucherId: data.booking_ref || data.displayTripId || data.itinerary_ref,
    displayTripId: data.displayTripId || data.itinerary_ref || '',
    itinerary: { title: data.trip_name },
  };

  return (
    <div className="min-h-screen bg-slate-100 py-8 px-4" data-testid="client-voucher-page">
      <div className="max-w-5xl mx-auto mb-4 flex flex-col items-end gap-2 print:hidden">
        <button
          type="button"
          onClick={handleDownload}
          disabled={downloading}
          data-testid="download-voucher-pdf-btn"
          className="inline-flex items-center gap-2 bg-[#2C3E35] hover:bg-[#1E2B25] disabled:opacity-60 text-white text-sm font-medium px-4 py-2 rounded-md transition-colors"
        >
          <Download className="h-4 w-4" />
          {downloading ? 'Generating PDF…' : 'Download Voucher PDF'}
        </button>
        {downloadError && (
          <p className="text-sm text-red-600" data-testid="download-voucher-pdf-error">{downloadError}</p>
        )}
      </div>

      <div className="max-w-5xl mx-auto">
        <div className="shadow-2xl mx-auto bg-white">
          <BrandedVoucherLayout voucher={layoutVoucher} company={company} qrValue={null} forPdf={false}>

            {/* 1 — Booking & coordinator */}
            <Section title="Booking & Coordinator" primary={primary} testId="section-booking">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {has(data.booking_ref) && <InfoCard label="Booking reference" value={data.booking_ref} accent={accent} mono />}
                {has(data.guest_name) && <InfoCard label="Lead guest" value={data.guest_name} accent={accent} />}
                {has(data.destination) && <InfoCard label="Destination" value={data.destination} accent={accent} />}
                {has(travelDates) && <InfoCard label="Travel dates" value={travelDates} accent={accent} />}
              </div>
              {hasCoord && (
                <div className="mt-3 grid grid-cols-2 md:grid-cols-3 gap-3">
                  {has(coord.name) && <InfoCard label="Coordinator" value={coord.name} accent={accent} />}
                  {has(coord.phone) && <InfoCard label="Phone" value={coord.phone} accent={accent} />}
                  {has(coord.whatsapp) && <InfoCard label="WhatsApp" value={coord.whatsapp} accent={accent} />}
                  {has(coord.email) && <InfoCard label="Email" value={coord.email} accent={accent} />}
                  {has(coord.available_hours) && <InfoCard label="Available hours" value={coord.available_hours} accent={accent} />}
                </div>
              )}
            </Section>

            {/* 1b — Weather (live, keyless) */}
            <WeatherSection destination={data.destination} days={days} primary={primary} />

            {/* 2 — Emergency contacts */}
            {has(data.emergency_contacts) && (
              <Section title="Emergency Contacts" primary={primary} testId="section-emergency">
                <div className="rounded-lg border border-slate-200 overflow-hidden">
                  {emergencyContacts.map((c, i) => <EmergencyRow key={i} c={c} />)}
                </div>
              </Section>
            )}

            {/* 3 — Per-day activities & vehicle */}
            {has(data.days) && (
              <Section title="Day-by-Day Plan" primary={primary} testId="section-days">
                <div className="space-y-4">
                  {days.map((day, i) => <DayCard key={i} day={day} index={i} primary={primary} accent={accent} />)}
                </div>
              </Section>
            )}

            {/* 4 — Accommodation confirmations */}
            {has(data.hotels) && (
              <Section title="Accommodation Confirmations" primary={primary} testId="section-accommodation">
                <div className="space-y-3">
                  {hotels.map((h, i) => <HotelCard key={i} h={h} index={i} />)}
                </div>
              </Section>
            )}

            {/* 5 — Flight timings (conditional) */}
            {has(data.flight_details) && (
              <Section title="Flight Timings" primary={primary} testId="section-flights">
                <div className="flex items-start gap-2">
                  <Plane className="h-4 w-4 mt-0.5 shrink-0" style={{ color: primary }} />
                  <Rich html={data.flight_details} />
                </div>
              </Section>
            )}

            {/* 6 — Things to carry (grouped by category) */}
            {carryCategories.length > 0 && (
              <Section title="Things to Carry" primary={primary} testId="section-carry">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5">
                  {carryCategories.map((c, i) => <CarryCategoryBlock key={i} name={c.name} items={c.items} />)}
                </div>
              </Section>
            )}

            {/* 7 — Transport options */}
            {has(data.transport_options) && (
              <Section title="Transport Options" primary={primary} testId="section-transport">
                <div className="rounded-lg border border-slate-200 overflow-hidden">
                  {transportOptions.map((t, i) => <TransportRow key={i} t={t} primary={primary} />)}
                </div>
              </Section>
            )}

            {/* 8 — Recommended places */}
            {has(data.recommended_places) && (
              <Section title="Recommended Places" primary={primary} testId="section-places">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {recommendedPlaces.map((p, i) => <PlaceCard key={i} p={p} accent={accent} />)}
                </div>
              </Section>
            )}

            {/* 9 — Local currency */}
            {hasCurrency && (
              <Section title="Local Currency" primary={primary} testId="section-currency">
                <div className="flex items-start gap-2">
                  <Wallet className="h-4 w-4 mt-0.5 shrink-0" style={{ color: primary }} />
                  <div className="flex-1">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
                      {has(cur.currency_name) && <InfoCard label="Currency" value={cur.currency_name} accent={accent} />}
                      {has(cur.currency_code) && <InfoCard label="Code" value={cur.currency_code} accent={accent} mono />}
                      {has(cur.currency_symbol) && <InfoCard label="Symbol" value={cur.currency_symbol} accent={accent} />}
                      {cur.exchange_rate != null && <InfoCard label="Rate (INR/unit)" value={String(cur.exchange_rate)} accent={accent} mono />}
                    </div>
                    {has(cur.atm_tips) && <p className="text-sm text-slate-600 mb-1"><span className="text-slate-400">ATM tips: </span>{cur.atm_tips}</p>}
                    {has(cur.exchange_tips) && <p className="text-sm text-slate-600"><span className="text-slate-400">Exchange tips: </span>{cur.exchange_tips}</p>}
                  </div>
                </div>
              </Section>
            )}

            {/* 10 — Voucher notes / T&C */}
            {(has(data.voucher_notes) || has(data.terms)) && (
              <Section title="Notes & Terms" primary={primary} testId="section-notes-terms">
                {has(data.voucher_notes) && <div className="mb-4"><Rich html={data.voucher_notes} /></div>}
                {has(data.terms) && (
                  <div className="pt-3 border-t border-slate-200">
                    <div className="text-[11px] font-bold uppercase mb-2" style={{ letterSpacing: '0.18em', color: accent }}>Terms &amp; Conditions</div>
                    <Rich html={data.terms} />
                  </div>
                )}
              </Section>
            )}

          </BrandedVoucherLayout>
        </div>
      </div>
    </div>
  );
}
